import { useTranslation } from 'react-i18next'
import React, { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Dialog, DialogContent, Button, IconButton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer } from '@material-ui/core'
import { Close, Add, GetApp } from '@material-ui/icons'
import { Image, Spinner } from 'ui'
import * as XLSX from 'xlsx'
import useStore from 'store'
import { importCreateUsers, importUpdateUsers, importChildUsers, RowStatus } from './importUsersApi'
import Import from 'assets/icons/Import.svg'

// --------------- Styled components ---------------

const Header = styled.div`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: center;
  border-bottom: 0.909px solid #E2E8F0;
  background: rgba(241, 245, 249, 0.30);
`
const Title = styled.div`
  color: #0F172A;
  font-family: Rubik;
  font-size: 20px;
  font-weight: 500;
  line-height: 30px;
`
const StyledClose = styled(Close)`
  color: #000;
  font-size: 20px;
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
`
const DropZone = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  border-radius: 8px;
  border: 2px dashed ${p => p.active ? '#2563EB' : '#CBD5E1'};
  background: ${p => p.active ? '#EFF6FF' : '#F8FAFC'};
  cursor: pointer;
  transition: all 0.15s;
`
const DropZoneText = styled.div`
  color: #64748B;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
`
const DropZoneHint = styled.div`
  color: #94A3B8;
  font-family: Rubik;
  font-size: 12px;
`
const BrowseLink = styled.span`
  color: #2563EB;
  cursor: pointer;
  text-decoration: underline;
`
const PreviewTitle = styled.div`
  color: #0F172A;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 600;
`
const PreviewCount = styled.span`
  color: #64748B;
  font-weight: 400;
  margin-right: 6px;
`
const TableContainerStyled = styled(TableContainer)`
  max-height: 360px;
  border-radius: 8px;
  border: 0.909px solid #E2E8F0;
`
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 4px;
`
const SaveButton = styled(Button)`
  display: flex;
  padding: 8px 24px;
  border-radius: 8px;
  background: #2563EB;
  color: #FFF;
  font-family: Rubik;
  font-size: 15px;
  font-weight: 500;
`
const CancelButton = styled(Button)`
  display: flex;
  padding: 8px 24px;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
  color: #364153;
  font-family: Rubik;
  font-size: 15px;
  font-weight: 500;
`
const ErrorText = styled.div`
  color: #DC2626;
  font-family: Rubik;
  font-size: 13px;
`
const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #E2E8F0;
  overflow: hidden;
`
const ProgressFill = styled.div<{ pct: number }>`
  height: 100%;
  border-radius: 999px;
  background: #2563EB;
  width: ${p => p.pct}%;
  transition: width 0.2s ease;
`
const ProgressLabel = styled.div`
  color: #64748B;
  font-family: Rubik;
  font-size: 13px;
  text-align: left;
`
const ErrorDetail = styled.div`
  color: #DC2626;
  font-family: Rubik;
  font-size: 11px;
  margin-top: 4px;
  white-space: pre-line;
  line-height: 1.6;
`
const StatusBadge = styled.div<{ status: RowStatus }>`
  display: inline-flex;
  padding: 2px 10px;
  font-family: Rubik;
  font-size: 12px;
  font-weight: 500;
  background: ${p => p.status === 'success' ? '#DCFCE7' : '#FEE2E2'};
  color: ${p => p.status === 'success' ? '#16A34A' : '#DC2626'};
`
const ImportIconImage = styled(Image)`
  width: 32px;
  height: 32px;
`
const LogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const LogTitle = styled.div`
  color: #0F172A;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 600;
`
const DownloadLogLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #2563EB;
  font-family: Rubik;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  svg {
    font-size: 16px;
  }
`
const LogContainer = styled.div`
  max-height: 160px;
  overflow-y: auto;
  padding: 8px 12px;
  border-radius: 8px;
  border: 0.909px solid #E2E8F0;
  background: #0F172A;
`
const LogLine = styled.div`
  color: #D1FAE5;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  /* @noflip */ direction: rtl;
  text-align: right;
`

// --------------- File type detection & column mapping ---------------
// Three known templates are supported, auto-detected from the header row:
//  - "create":  new users import           (import setp 1.xlsx)
//  - "update":  update existing users      (update setp 2.xlsx)
//  - "child":   update/link children       (update setp 3 childs.xlsx)
export type FileType = 'create' | 'update' | 'child'

const CREATE_COLUMN_MAP: Record<string, string> = {
  'שם פרטי':                          'givenName',
  'שם משפחה':                         'familyName',
  'שם אמצעי':                         'middleName',
  'כינוי':                             'nickName',
  'תאריך לידה':                        'dateOfBirth',
  'תעודת זהות':                        'israeliId',
  'מין':                               'gender',
  'מצב משפחתי':                       'maritalStatus',
  'נייד':                              'mainMobilePhone',
  'טלפון נייד':                        'mainMobilePhone',
  'נייד נוסף':                         'secondaryMobilePhone',
  'טלפון נוסף':                        'otherPhone',
  'דואר אלקטרוני':                     'email',
  'ישוב':                              'addressCity',
  'רחוב':                              'addressStreet',
  'מספר בית':                          'addressHouseNumber',
  'ממ"ד/מקלט':                         'shelter',
  'איש קשר במקרה אסון':               'contactPerson1',
  'טלפון איש קשר במקרה אסון':         'contactPersonCellPhone1',
  'איש קשר 2 במקרה אסון':             'contactPerson2',
  'טלפון איש קשר 2 במקרה אסון':       'contactPersonCellPhone2',
  'תגיות':                             'tagsOthers',
  'תפקידים':                           'tagsRoles',
  'סוג משתמש':                         'tagsStatus',
  'חבר מקומי הודעות':                 'isLocalMember',
}

export const UPDATE_COLUMN_MAP: Record<string, string> = {
  'שם פרטי':                          'givenName',
  'שם משפחה':                         'familyName',
  'שם אמצעי':                         'middleName',
  'כינוי':                             'nickName',
  'תאריך לידה':                        'dateOfBirth',
  'תעודת זהות':                        'israeliId',
  'מין':                               'gender',
  'מצב משפחתי':                       'maritalStatus',
  'טלפון נוסף':                        'otherPhone',
  'ישוב':                              'addressCity',
  'רחוב':                              'addressStreet',
  'מספר בית':                          'addressHouseNumber',
  'ממ"ד/מקלט':                         'shelter',
  'איש קשר במקרה אסון':               'contactPerson1',
  'טלפון איש קשר במקרה אסון':         'contactPersonCellPhone1',
  'איש קשר 2 במקרה אסון':             'contactPerson2',
  'טלפון איש קשר 2 במקרה אסון':       'contactPersonCellPhone2',
  'תגיות':                             'tagsOthers',
  'תפקידים':                           'tagsRoles',
  'סוג משתמש':                         'tagsStatus',
  'חבר מקומי הודעות':                 'isLocalMember',
}

const CHILD_COLUMN_MAP: Record<string, string> = {
  'תעודת זהות הורה':                  'parentIdentityNumber',
  'תעודת זהות ילד':                   'israeliId',
  'שם פרטי ילד':                      'givenName',
  'שם משפחה ילד':                     'familyName',
  'שם אמצעי ילד':                     'middleName',
  'נייד ילד':                          'mainMobilePhone',
  'תאריך לידה ילד':                   'dateOfBirth',
  'מגדר ילד':                          'gender',
}

const COLUMN_MAPS: Record<FileType, Record<string, string>> = {
  create: CREATE_COLUMN_MAP,
  update: UPDATE_COLUMN_MAP,
  child: CHILD_COLUMN_MAP,
}

// Keys, not text: these are module constants, so they cannot hold text for a
// language that is chosen at runtime.
export const FILE_TYPE_LABELS: Record<FileType, string> = {
  create: 'population:import_mode_create',
  update: 'population:import_mode_update',
  child: 'population:import_mode_child',
}

const normalizeHeader = (h: any): string => String(h ?? '')
  .normalize('NFKC')
  .replace(/[​-‏‪-‮⁦-⁩﻿]/g, '') // strip invisible RTL/LRM/BOM marks Excel sometimes embeds
  .replace(/\*/g, '')
  .replace(/[“”״]/g, '"') // curly quotes / Hebrew gershayim -> plain "
  .replace(/\s+/g, ' ')
  .trim()

const ALL_KNOWN_HEADERS = new Set([
  ...Object.keys(CREATE_COLUMN_MAP),
  ...Object.keys(UPDATE_COLUMN_MAP),
  ...Object.keys(CHILD_COLUMN_MAP),
].map(normalizeHeader))

const detectHeaderRowIndex = (rows: any[][]): number => {
  let best = 0, bestScore = -1
  for (let i = 0; i < Math.min(5, rows.length); i++) {
    const score = (rows[i] || []).filter((h: any) => ALL_KNOWN_HEADERS.has(normalizeHeader(h))).length
    if (score > bestScore) { bestScore = score; best = i }
  }
  return best
}

const detectFileType = (headerRow: any[]): FileType | null => {
  const header = (headerRow || []).map(normalizeHeader)
  if (header[0] === 'תעודת זהות הורה') return 'child'
  if ((header.includes('נייד') || header.includes('טלפון נייד')) && header.includes('דואר אלקטרוני')) return 'create'
  if (header.includes('תעודת זהות')) return 'update'
  return null
}

const formatDateCell = (raw: any): string => {
  if (raw instanceof Date) {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(raw.getUTCDate())}/${pad(raw.getUTCMonth() + 1)}/${raw.getUTCFullYear()}`
  }
  return raw != null ? String(raw).trim() : ''
}

const DATE_FIELDS = new Set(['dateOfBirth'])

const mapRows = (rows: any[][], headerRowIndex: number, type: FileType): any[] => {
  const map = COLUMN_MAPS[type]
  const normalizedMap: Record<string, string> = {}
  Object.entries(map).forEach(([key, field]) => { normalizedMap[normalizeHeader(key)] = field })

  const headerRow: string[] = (rows[headerRowIndex] || []).map(normalizeHeader)

  const colIndex: Record<string, number> = {}
  headerRow.forEach((h, i) => {
    const field = normalizedMap[h]
    if (field) colIndex[field] = i
  })

  return rows
    .slice(headerRowIndex + 1)
    .filter(row => row.some(cell => cell != null && cell !== ''))
    .map(row => {
      const record: any = {}
      for (const [field, idx] of Object.entries(colIndex)) {
        const raw = row[idx as number]
        record[field] = DATE_FIELDS.has(field) ? formatDateCell(raw) : (raw != null ? String(raw).trim() : '')
      }
      return record
    })
}

type ParsedFile = { type: FileType; rows: any[] } | { type: null; rows: never[] }

const parseSheetRows = (rows: any[][]): ParsedFile => {
  const headerRowIndex = detectHeaderRowIndex(rows)
  const type = detectFileType(rows[headerRowIndex] || [])
  if (!type) return { type: null, rows: [] }
  return { type, rows: mapRows(rows, headerRowIndex, type) }
}

const parseXlsx = (buffer: ArrayBuffer): ParsedFile => {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true })
  const ws = wb.Sheets[wb.SheetNames[0]]
  const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 })
  return parseSheetRows(rows)
}

const parseCsv = (text: string): ParsedFile => {
  const clean = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text
  const wb = XLSX.read(clean, { type: 'string', cellDates: true })
  const ws = wb.Sheets[wb.SheetNames[0]]
  const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 })
  return parseSheetRows(rows)
}

// --------------- Component ---------------

type Props = {
  open: boolean
  onClose: () => void
  communityId: number
  onImported: () => void
}

const PREVIEW_COLUMNS: Record<FileType, { label: string; field: string }[]> = {
  create: [
    { label: 'population:given_name', field: 'givenName' },
    { label: 'population:family_name', field: 'familyName' },
    { label: 'population:id_short', field: 'israeliId' },
    { label: 'population:mobile', field: 'mainMobilePhone' },
    { label: 'population:email', field: 'email' },
    { label: 'population:birth_date', field: 'dateOfBirth' },
  ],
  update: [
    { label: 'population:given_name', field: 'givenName' },
    { label: 'population:family_name', field: 'familyName' },
    { label: 'population:id_short', field: 'israeliId' },
    { label: 'population:city', field: 'addressCity' },
    { label: 'population:birth_date', field: 'dateOfBirth' },
  ],
  child: [
    { label: 'population:preview_parent_id', field: 'parentIdentityNumber' },
    { label: 'population:preview_child_id', field: 'israeliId' },
    { label: 'population:preview_child_given_name', field: 'givenName' },
    { label: 'population:preview_child_family_name', field: 'familyName' },
    { label: 'population:preview_child_mobile', field: 'mainMobilePhone' },
    { label: 'population:preview_child_birth_date', field: 'dateOfBirth' },
  ],
}

const ACTION_LABELS: Record<FileType, string> = {
  create: 'population:import_action_create',
  update: 'population:import_action_update',
  child: 'population:import_action_child',
}

const ImportUsersModal: FC<Props> = ({ open, onClose, communityId, onImported }) => {
  const { t } = useTranslation('population')
  const { imsUsersStore } = useStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [rows, setRows] = useState<any[]>([])
  const [fileType, setFileType] = useState<FileType | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState({ imported: 0, total: 0 })
  const [rowStatuses, setRowStatuses] = useState<Record<number, { status: RowStatus; message?: string; detail?: string; code?: string }>>({})
  const [log, setLog] = useState<string[]>([])
  const [completed, setCompleted] = useState(false)
  const logContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [log])

  const reset = () => {
    setRows([])
    setFileType(null)
    setError(null)
    setSaving(false)
    setProgress({ imported: 0, total: 0 })
    setRowStatuses({})
    setLog([])
    setCompleted(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const processFile = (file: File) => {
    setError(null)
    const isCsv = file.name.toLowerCase().endsWith('.csv')
    const reader = new FileReader()
    reader.onload = e => {
      try {
        const parsed = isCsv
          ? parseCsv(e.target!.result as string)
          : parseXlsx(e.target!.result as ArrayBuffer)

        if (!parsed.type) {
          setError(t`import_format_unknown`)
          return
        }
        if (parsed.rows.length === 0) {
          setError(t`import_no_rows`)
          return
        }
        setFileType(parsed.type)
        setRows(parsed.rows)
      } catch {
        setError(t`import_read_error`)
      }
    }
    if (isCsv) {
      reader.readAsText(file, 'UTF-8')
    } else {
      reader.readAsArrayBuffer(file)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  const handleImport = async () => {
    if (!fileType) return

    setSaving(true)
    setError(null)
    setProgress({ imported: 0, total: rows.length })
    setRowStatuses({})
    setLog([t('import_starting', { action: t(ACTION_LABELS[fileType]), count: rows.length, mode: t(FILE_TYPE_LABELS[fileType]) })])

    const onProgress = (index: number, total: number, status: RowStatus, message?: string, detail?: string, code?: string) => {
      setRowStatuses(prev => ({ ...prev, [index]: { status, message, detail, code } }))
      setProgress(prev => ({ ...prev, imported: prev.imported + 1, total }))
    }
    const onLog = (message: string) => setLog(prev => [...prev, message])

    try {
      const res = fileType === 'create'
        ? await importCreateUsers(rows, communityId, onProgress, onLog)
        : fileType === 'update'
        ? await importUpdateUsers(rows, communityId, imsUsersStore.allUsers, onProgress, onLog)
        : await importChildUsers(rows, communityId, imsUsersStore.allUsers, onProgress, onLog)

      onLog(res.success ? t`import_done_ok` : t`import_done_errors`)

      setSaving(false)
      setCompleted(true)
      onImported()
    } catch {
      onLog(t`import_unexpected`)
      setError(t`import_failed`)
      setSaving(false)
    }
  }

  const STATUS_LABELS: Record<RowStatus, string> = {
    success: t`success`,
    error: t`error`,
  }

  const handleDownloadLog = () => {
    const headers = [t`log_col_row`, t`log_col_identity`, t`log_col_user`, t`log_col_error_code`, t`log_col_error_desc`, t`log_col_status`, t`log_col_details`]

    const data = rows
      .map((row, i) => ({ row, i, st: rowStatuses[i] }))
      .filter(({ st }) => st?.status === 'error')
      .map(({ row, i, st }) => {
        const rowLabelPrefix = t('log_row_prefix', { row: i + 1 })
        const additionalDetails = log
          .filter(line => line.startsWith(rowLabelPrefix))
          .map(line => line.slice(rowLabelPrefix.length).trim())
          .join(' | ')

        return [
          i + 1,
          row.israeliId || '',
          [row.givenName, row.familyName].filter(Boolean).join(' '),
          st?.code || '',
          st?.message || '',
          STATUS_LABELS[st!.status],
          additionalDetails,
        ]
      })

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data])
    ws['!cols'] = headers.map(() => ({ wch: 22 }))
    wb.Workbook = { Views: [{ RTL: true }] }
    XLSX.utils.book_append_sheet(wb, ws, t`log_sheet_name`)
    XLSX.writeFile(wb, `יומן-ייבוא-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  const previewColumns = fileType ? PREVIEW_COLUMNS[fileType] : []

  return (
    <Dialog open={open} fullWidth maxWidth="lg" scroll="body">
      <Header>
        <Title>{t`import_title`}</Title>
        <IconButton onClick={handleClose}><StyledClose /></IconButton>
      </Header>

      <DialogContent>
        <Body>
          {rows.length === 0 && (
            <DropZone
              active={dragOver}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <ImportIconImage src={Import} />
              <DropZoneText>
                {t`drop_file`}<BrowseLink>{t`browse_file`}</BrowseLink>
              </DropZoneText>
              <DropZoneHint>{t`drop_hint_autodetect`}</DropZoneHint>
              <DropZoneHint>{t`drop_hint_formats`}</DropZoneHint>
            </DropZone>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx,.csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {error && <ErrorText>{error}</ErrorText>}

          {saving && (
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill pct={progress.total > 0 ? Math.round((progress.imported / progress.total) * 100) : 0} />
              </ProgressBar>
              <ProgressLabel>
                {progress.imported} / {progress.total} ({progress.total > 0 ? Math.round((progress.imported / progress.total) * 100) : 0}%)
              </ProgressLabel>
            </ProgressContainer>
          )}

          {log.length > 0 && (
            <ProgressContainer>
              <LogHeader>
                <LogTitle>{t`log_title`}</LogTitle>
                <DownloadLogLink onClick={handleDownloadLog}>
                  <GetApp />
                  {t`log_download`}
                </DownloadLogLink>
              </LogHeader>
              <LogContainer ref={logContainerRef}>
                {log.map((line, i) => <LogLine key={i}>{line}</LogLine>)}
              </LogContainer>
            </ProgressContainer>
          )}

          {rows.length > 0 && fileType && (
            <>
              <PreviewTitle>
                {t('preview_title', { mode: t(FILE_TYPE_LABELS[fileType]) })}
                <PreviewCount>{t('preview_rows', { count: rows.length })}</PreviewCount>
              </PreviewTitle>
              <TableContainerStyled>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {previewColumns.map(col => (
                        <TableCell key={col.field}>{t(col.label)}</TableCell>
                      ))}
                      <TableCell>{t`log_col_status`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={i}>
                        {previewColumns.map(col => (
                          <TableCell key={col.field}>{row[col.field]}</TableCell>
                        ))}
                        <TableCell>
                          {rowStatuses[i]?.status === 'success' && <StatusBadge status="success">{t`status_done`}</StatusBadge>}
                          {rowStatuses[i]?.status === 'error' && (
                            <>
                              <StatusBadge status="error">{rowStatuses[i].message || t`error`}</StatusBadge>
                              {rowStatuses[i].detail && <ErrorDetail>{rowStatuses[i].detail}</ErrorDetail>}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainerStyled>
            </>
          )}

          <Actions>
            <CancelButton onClick={handleClose} disabled={saving}>{completed ? t`close` : t`cancel`}</CancelButton>
            {rows.length > 0 && fileType && !completed && (
              <SaveButton onClick={handleImport} disabled={saving}>
                {saving ? <Spinner size={20} /> : t('import_button', { action: t(ACTION_LABELS[fileType]), count: rows.length })}
              </SaveButton>
            )}
          </Actions>
        </Body>
      </DialogContent>
    </Dialog>
  )
}

export default ImportUsersModal
